import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaFacebookSquare, 
  FaEnvelopeOpenText, 
  FaRegCalendarAlt,
  FaRegNewspaper,
  FaPalette
} from 'react-icons/fa';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardFooter from '../../components/DashboardFooter';
import './marketing.css';
import { auth } from '../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../auth/firebase';
import cacheManager from '../../auth/cache_manager';

const Marketing = () => {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Marketing tiles configuration
  const marketingTiles = [
    {
      title: 'Social Media Marketing',
      icon: <FaFacebookSquare className="marketing_tile_icon" />,
      description: 'Manage social media campaigns across platforms',
      path: '/admin/content-creation/marketing/social'
    },
    {
      title: 'Email Marketing',
      icon: <FaEnvelopeOpenText className="marketing_tile_icon" />,
      description: 'Create and manage email campaigns',
      path: '/admin/content-creation/marketing/email'
    },
    {
      title: 'Marketing Calendar',
      icon: <FaRegCalendarAlt className="marketing_tile_icon" />,
      description: 'Plan and schedule marketing activities',
      path: '/admin/content-creation/marketing/calendar'
    },
    {
      title: 'Marketing Posts',
      icon: <FaRegNewspaper className="marketing_tile_icon" />,
      description: 'Create and manage marketing posts',
      path: '/admin/content-creation/marketing/posts'
    },
    {
      title: 'Marketing Designs',
      icon: <FaPalette className="marketing_tile_icon" />,
      description: 'Create and manage marketing designs',
      path: '/admin/content-creation/marketing/designs'
    }
  ];

  // # Handle tile click
  const handleTileClick = (path) => {
    navigate(path);
  };

  return (
    <div className={`marketing_page_container ${isDarkMode ? 'marketing_dark_mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="marketing_container">
        <header className="marketing_header">
          <h1>Marketing Management</h1>
          <p>Create and manage your marketing campaigns</p>
        </header>

        <div className="marketing_tiles_grid">
          {marketingTiles.map((tile, index) => (
            <div 
              key={index}
              className="marketing_tile"
              onClick={() => handleTileClick(tile.path)}
            >
              <div className="marketing_tile_icon_wrapper">
                {tile.icon}
              </div>
              <h3>{tile.title}</h3>
              <p>{tile.description}</p>
            </div>
          ))}
        </div>
      </div>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default Marketing;
